<template>
  <v2-modal
    :show="show"
    title="Invite Teammate"
    size="md"
    @close="closeModal"
  >
    <p class="v2-text-sm v2-text-muted v2-mb-4">
      Enter teammate details to send an invitation
    </p>

    <form @submit.prevent="onSubmit">
      <div class="v2-flex v2-flex-col v2-gap-4">
        <v2-input
          v-model="form.email"
          label="Email"
          placeholder="Enter email address"
          :disabled="isSubmitting"
          :error="errors.email"
          @blur="validateField('email')"
        />
        <v2-input
          v-model="form.firstName"
          label="First Name"
          placeholder="Enter first name"
          :disabled="isSubmitting"
          :error="errors.firstName"
          @blur="validateField('firstName')"
        />
        <v2-input
          v-model="form.lastName"
          label="Last Name"
          placeholder="Enter last name"
          :disabled="isSubmitting"
          :error="errors.lastName"
          @blur="validateField('lastName')"
        />
      </div>

      <div class="v2-flex v2-justify-end v2-gap-2 v2-mt-6">
        <v2-button
          variant="outline"
          :disabled="isSubmitting"
          @click="closeModal"
        >
          Cancel
        </v2-button>
        <v2-button
          type="submit"
          :disabled="!isFormValid || isSubmitting"
          :loading="isSubmitting"
        >
          {{ isSubmitting ? 'Sending...' : 'Send Invite' }}
        </v2-button>
      </div>
    </form>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import { INVITE_RETAILER_TEAMMATE } from '@/store/modules/connections.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'

const { RETAILER_TEAMMATES } = constants.TRACKS.ACTIONS
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'V2InviteTeammateModal',
  components: {
    V2Modal,
    V2Input,
    V2Button,
  },
  props: {
    show: { type: Boolean, default: false },
    retailerId: { type: String, default: null },
  },
  data() {
    return {
      isSubmitting: false,
      form: {
        email: '',
        firstName: '',
        lastName: '',
      },
      errors: {
        email: '',
        firstName: '',
        lastName: '',
      },
    }
  },
  computed: {
    isFormValid() {
      return this.form.email
        && EMAIL_REGEX.test(this.form.email)
        && this.form.firstName
        && this.form.lastName
        && this.retailerId
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.onModalOpen()
      } else {
        this.onModalClose()
      }
    },
  },
  methods: {
    onModalOpen() {
      analytics.track(RETAILER_TEAMMATES.BRAND_VIEWS_INVITE_TEAMMATE_MODAL, {
        retailerId: this.retailerId,
      })
    },
    onModalClose() {
      analytics.track(RETAILER_TEAMMATES.BRAND_CLOSES_INVITE_TEAMMATE_MODAL, {
        retailerId: this.retailerId,
      })
      this.resetForm()
    },
    validateEmail() {
      if (!this.form.email) {
        this.errors.email = 'Email is required'
        return
      }
      const isValidFormat = EMAIL_REGEX.test(this.form.email)
      this.errors.email = isValidFormat ? '' : 'Please enter a valid email'
    },
    validateRequiredField(field, label) {
      this.errors[field] = this.form[field] ? '' : `${label} is required`
    },
    validateField(field) {
      const requiredFieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
      }
      if (field === 'email') {
        this.validateEmail()
        return
      }
      if (requiredFieldLabels[field]) {
        this.validateRequiredField(field, requiredFieldLabels[field])
      }
    },
    trackAnalytics(event, extraData = {}) {
      analytics.track(event, {
        retailerId: this.retailerId,
        email: this.form.email,
        ...extraData,
      })
    },
    buildInvitePayload() {
      return {
        retailerId: this.retailerId,
        payload: {
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
        },
      }
    },
    extractErrorMessage(err) {
      const responseData = err?.response?.data
      return responseData?.details?.message
        || responseData?.message
        || 'Failed to send invitation'
    },
    handleInviteSuccess() {
      apiToastSuccess('Teammate invitation sent successfully')
      this.trackAnalytics(RETAILER_TEAMMATES.BRAND_INVITE_TEAMMATE_SUCCESS)
      this.$emit('invited')
      this.closeModal()
    },
    handleInviteError(err) {
      const errorMessage = this.extractErrorMessage(err)
      apiToastWarning(errorMessage)
      this.trackAnalytics(RETAILER_TEAMMATES.BRAND_INVITE_TEAMMATE_FAILED, { error: errorMessage })
    },
    async onSubmit() {
      if (!this.isFormValid) return

      this.trackAnalytics(RETAILER_TEAMMATES.BRAND_SUBMITS_INVITE_TEAMMATE)
      this.isSubmitting = true
      try {
        await this.$store.dispatch(INVITE_RETAILER_TEAMMATE, this.buildInvitePayload())
        this.handleInviteSuccess()
      } catch (err) {
        this.handleInviteError(err)
      } finally {
        this.isSubmitting = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
    resetForm() {
      this.form = { email: '', firstName: '', lastName: '' }
      this.errors = { email: '', firstName: '', lastName: '' }
    },
  },
}
</script>
